import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLIframes = () => {
  return (
    <div className="html-container">
      <h1>HTML – Iframes</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          An <code>&lt;iframe&gt;</code> (inline frame) allows embedding another HTML page within the current page.
          It is commonly used to embed external content such as videos, maps, or advertisements.
        </p>
      </section>

      {/* 2. Syntax */}
      <section id="syntax">
        <h2>2. Syntax</h2>
        <pre>
          <code>{`<iframe src="URL" width="WIDTH" height="HEIGHT" title="DESCRIPTION"></iframe>`}</code>
        </pre>
      </section>

      {/* 3. Example */}
      <section id="example">
        <h2>3. Example</h2>
        <p>Embedding a YouTube video using an iframe:</p>
        <pre>
          <code>{`<iframe 
  width="560" 
  height="315" 
  src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
  title="YouTube video player" 
  frameborder="0" 
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
  allowfullscreen>
</iframe>`}</code>
        </pre>
      </section>

      {/* 4. Common Attributes */}
      <section id="attributes">
        <h2>4. Common Attributes</h2>
        <ul>
          <li><code>src</code>: URL of the page to embed (required).</li>
          <li><code>width</code> & <code>height</code>: Size of the iframe.</li>
          <li><code>title</code>: Descriptive text for accessibility.</li>
          <li><code>frameborder</code>: Sets the border (deprecated, use CSS instead).</li>
          <li><code>allowfullscreen</code>: Allows full-screen mode.</li>
          <li><code>loading</code>: <code>lazy</code> or <code>eager</code> for performance optimization.</li>
          <li><code>sandbox</code>: Adds security restrictions (e.g., <code>allow-scripts</code>).</li>
        </ul>
      </section>

      {/* 5. Security Considerations */}
      <section id="security">
        <h2>5. Security Considerations</h2>
        <ul>
          <li>Use <code>sandbox</code> to prevent malicious content.</li>
          <li>Specify <code>referrerpolicy</code> to control information sent to external sites.</li>
          <li>Avoid embedding untrusted sources.</li>
          <li>Prefer HTTPS URLs to ensure secure connections.</li>
        </ul>
      </section>

      {/* 6. Responsive Iframes */}
      <section id="responsive">
        <h2>6. Responsive Iframes</h2>
        <p>Use CSS to make iframes responsive:</p>
        <pre>
          <code>{`.iframe-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 ratio */
  height: 0;
  overflow: hidden;
}

.iframe-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}`}</code>
        </pre>
        <div className="iframe-container">
          <iframe 
            src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
            title="YouTube video" 
            allowFullScreen
          ></iframe>
        </div>
      </section>

      {/* 7. Best Practices */}
      <section id="best-practices">
        <h2>7. Best Practices</h2>
        <ul>
          <li>Always provide a descriptive <code>title</code> for accessibility.</li>
          <li>Use <code>loading="lazy"</code> for better performance.</li>
          <li>Limit iframe size and ensure it fits within your layout.</li>
          <li>Apply responsive styling for mobile and desktop views.</li>
          <li>Use <code>sandbox</code> for embedding third-party content.</li>
        </ul>
      </section>

      {/* 8. Summary */}
      <section id="summary">
        <h2>8. Summary</h2>
        <ul>
          <li><code>&lt;iframe&gt;</code> embeds external HTML pages within your page.</li>
          <li>Supports attributes like <code>src</code>, <code>width</code>, <code>height</code>, <code>title</code>, and <code>sandbox</code>.</li>
          <li>Ensure accessibility, security, and responsiveness.</li>
          <li>Best practice: limit iframe usage for performance and user experience.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLIframes;
